import { useState, useEffect, useRef, useMemo } from 'react'
import { useAuthStore } from '@/stores/auth-store'

// Iframe renders at 1200×675 (16:9). Scale is measured per-card via ResizeObserver
// so the iframe fills the aspect-video container exactly at any card width.
const PREVIEW_WIDTH  = 1200
const PREVIEW_HEIGHT = 675 // 1200 × 9/16

interface LivePreviewIframeProps {
  componentKey: string
  defaultSettings: Record<string, unknown>
  faded?: boolean
}

export function LivePreviewIframe({ componentKey, defaultSettings, faded }: LivePreviewIframeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')
  const [scale, setScale] = useState(0)
  const tenantSlug = useAuthStore((s) => s.currentTenant?.slug)

  // IntersectionObserver: load iframe only when card is near viewport
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          setStatus('loading')
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // ResizeObserver: compute exact scale so iframe fills the aspect-video container
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      if (w > 0) setScale(w / PREVIEW_WIDTH)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const previewUrl = useMemo(() => {
    if (!tenantSlug) return null
    const publicBase =
      (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ??
      `https://${tenantSlug}.oneidol.id`
    const block = {
      id: 'preview',
      component: componentKey,
      enabled: true,
      order: 1,
      settings: defaultSettings,
    }
    return `${publicBase}/${tenantSlug}/preview?blocks=${encodeURIComponent(JSON.stringify([block]))}&demo=1`
  }, [tenantSlug, componentKey, defaultSettings])

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-video border-b border-border overflow-hidden bg-muted/30 ${faded ? 'opacity-40' : ''}`}
    >
      {/* Loading pulse */}
      {status === 'loading' && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {/* Error fallback */}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs text-muted-foreground/50">Preview tidak tersedia</span>
        </div>
      )}

      {/* Scaled iframe — rendered only when visible, url known, and scale measured */}
      {isVisible && previewUrl && scale > 0 && (
        <iframe
          src={previewUrl}
          title="component preview"
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
          style={{
            width: `${PREVIEW_WIDTH}px`,
            height: `${PREVIEW_HEIGHT}px`,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            border: 'none',
            pointerEvents: 'none',
            opacity: status === 'loaded' ? 1 : 0,
            transition: 'opacity 0.2s ease',
          }}
        />
      )}
    </div>
  )
}
